import { GenData, IGenSingle, QSingle } from '@educraft/base';
import { IExercise, ExerciseData, addResultSimple } from '../exercise';
import { PdfUnit, PdfRect, PdfBrush, ETextAlign } from '../../pdf/pdf';
import { GUtil } from '../../pdf/gutil';
import { makeCombinedSingle, generateUnique } from './exeUtil';

export type PairFactory = (gen: IGenSingle, genData: GenData) => { a: QSingle; b: QSingle };

export class ExerciseCompareBase implements IExercise {
  readonly fixedHeight = 0;
  readonly noWindow = false;
  readonly injectEq = true;
  readonly injectAbc = false;

  private marginColumn = PdfUnit.FromMillimeters(3);
  private marginValues = PdfUnit.FromMillimeters(1);
  private marginRow = PdfUnit.FromMillimeters(2);

  constructor(
    public readonly name: string,
    private createPair: PairFactory
  ) {}

  draw(data: ExerciseData): void {
    if (data.item.selEq.length === 0) return;

    const gen = makeCombinedSingle(data);

    // Adapter: generate pairs as a single "generate" call
    const pairGen = {
      generate: (genData: GenData) => this.createPair(gen, genData),
    };

    // Sample to measure
    let szA = 0,
      szB = 0,
      szY = 0;
    const samples: { a: QSingle; b: QSingle }[] = [];

    for (let i = 0; i < 20; i++) {
      const q = pairGen.generate(data.data);
      samples.push(q);

      const textA = q.a.texts[0].replace(/[\s=]+$/, '');
      const textB = q.b.texts[0].replace(/[\s=]+$/, '');
      const sizeA = data.page.measureString(textA, GUtil.exerciseFont);
      const sizeB = data.page.measureString(textB, GUtil.exerciseFont);

      if (i === 0) {
        szA = sizeA.width;
        szB = sizeB.width;
        szY = Math.max(sizeA.height, sizeB.height);
      } else {
        szA = Math.max(szA, sizeA.width);
        szB = Math.max(szB, sizeB.width);
        szY = Math.max(szY, Math.max(sizeA.height, sizeB.height));
      }
    }

    const szX = Math.max(szA, szB);

    const countX = Math.max(
      1,
      Math.floor(data.rect.width / (2 * szX + szY + this.marginValues * 2 + this.marginColumn))
    );
    const countY = Math.max(1, Math.floor(data.rect.height / (szY + this.marginRow)));
    const total = countX * countY;

    const array = generateUnique(
      pairGen,
      data.data,
      total,
      (q) => `${q.a.texts[0]}|${q.a.answer}|${q.b.texts[0]}|${q.b.answer}`,
      samples
    );

    const cellW = (data.rect.width + this.marginColumn) / countX;
    const cellH = (data.rect.height + this.marginRow) / countY;

    for (let x = 0; x < countX; x++) {
      for (let y = 0; y < countY; y++) {
        const index = y + x * countY;
        if (index >= array.length) break;
        const item = array[index];

        const r = new PdfRect(
          data.rect.x + x * cellW,
          data.rect.y + y * cellH,
          cellW - this.marginColumn,
          cellH - this.marginRow
        );

        const a = item.a.texts[0].replace(/[\s=]+$/, '');
        const b = item.b.texts[0].replace(/[\s=]+$/, '');
        const w = (r.width - r.height) / 2;

        // Determine comparison result
        const numA = parseFloat(item.a.answer);
        const numB = parseFloat(item.b.answer);
        if (!isNaN(numA) && !isNaN(numB)) {
          if (numA < numB) addResultSimple(data, `${a} < ${b}`);
          else if (numA > numB) addResultSimple(data, `${a} > ${b}`);
          else addResultSimple(data, `${a} = ${b}`);
        }

        const rA = new PdfRect(r.x, r.y, w, r.height);
        const rB = new PdfRect(r.x + w + r.height + 2 * this.marginValues, r.y, w, r.height);
        const rCC = new PdfRect(r.x + w + this.marginValues, r.y, r.height, r.height);

        data.page.drawString(
          a,
          GUtil.exerciseFont,
          PdfBrush.Black,
          null,
          rA,
          ETextAlign.CenterRight
        );
        data.page.drawString(
          b,
          GUtil.exerciseFont,
          PdfBrush.Black,
          null,
          rB,
          ETextAlign.CenterLeft
        );
        data.colorSchema.drawResult(data.page, rCC);
      }
    }
  }
}
